import { flattenDeep, isNull, noop } from 'lodash/fp';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { DocumentNameDB } from '../../admin/documents/store';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { fetchAllDropdownListsStarted } from '../../admin/dropdown-lists/store';
import { MyRiskTolerance, RiskAssociatedValue, RiskField, RiskToleranceDataset, RiskTolerance as RiskToleranceType, SelectedRiskField, RiskAssociated, DatasetFieldSection } from '../../admin/risk-tolerance/store';
import { DatasetDefinition, DocumentSpecificHiddenFields, FormDatasetFields, HiddenFields, isGroupField } from '../../datasets/store';
import styles from './RiskTolerance.module.scss';
import { RiskToleranceConfig } from './RiskToleranceConfig';
import { RiskToleranceDatasetsMenu } from './RiskToleranceDatasetsMenu';
import { PageLoadingDots } from '../loading-dots/PageLoadingDots';
import { HelpModal } from './HelpModal';
import { CustomTooltip } from '../tooltip';

export const flattenFormDefinitionFields = (fields: FormDatasetFields) => flattenDeep(Object.entries(fields).map(([sectionId, section]) => section.map(field => isGroupField(field) ? field.children.map(field => ({ field, sectionId })) : { field, sectionId }))) as DatasetFieldSection[];

interface RiskToleranceProps {
    datasetId: number;
    setRiskToleranceField: (riskField: SelectedRiskField) => void;
    riskTolerance: (RiskToleranceType | MyRiskTolerance)[] | null;
    selectedRiskField: RiskField | null;
    selectedField: SelectedRiskField | null;
    isLoading: boolean;
    datasetDefinitions: DatasetDefinition[];
    allRiskToleranceDatasets: RiskToleranceDataset[];
    setWeighting: (weighting: number) => void;
    setFallbackPosition: (fallbackPosition: number) => void;
    updateRiskConfig: (riskAssociated: RiskAssociated, value: RiskAssociatedValue, selectedCurrency?: string) => void;
    buttonWrapper: ReactNode;
    hiddenFields?: HiddenFields;
    showRiskFieldToggles?: boolean;
    toggleRiskField?: (datasetId: number, fieldId: string) => void;
    documentHiddenFields: DocumentSpecificHiddenFields;
    allDocumentNames: DocumentNameDB[];
    agreementTypeDatasetId: number | null;
}

export const RiskTolerance: React.FC<RiskToleranceProps> = ({ datasetId,
    setRiskToleranceField,
    riskTolerance,
    selectedRiskField,
    selectedField,
    isLoading,
    datasetDefinitions,
    allRiskToleranceDatasets,
    setWeighting,
    setFallbackPosition,
    buttonWrapper,
    updateRiskConfig,
    hiddenFields,
    showRiskFieldToggles = false,
    toggleRiskField = noop,
    documentHiddenFields,
    allDocumentNames,
    agreementTypeDatasetId
}) => {
    const [helpModalOpen, setHelpModalOpen] = useState<boolean>(false);
    useFetchStarted([fetchAllDropdownListsStarted()]);

    const agreementType = useMemo(() => allRiskToleranceDatasets.find(riskDataset => riskDataset.datasetId === datasetId)?.agreementType || '', [allRiskToleranceDatasets, datasetId]);

    const availableDatasets = useMemo(() => riskTolerance && riskTolerance.map(({ datasetId, datasetTitle }) => ({ datasetId, datasetTitle })), [riskTolerance]);

    const openHelpModal = useCallback(() => setHelpModalOpen(true), []);
    const closeHelpModal = useCallback(() => setHelpModalOpen(false), []);

    const selectedConfig = useMemo(() => {
        if (isNull(selectedRiskField)) {
            return null;
        }
        return (
            <RiskToleranceConfig
                riskField={selectedRiskField}
                setWeighting={setWeighting}
                setFallbackPosition={setFallbackPosition}
                updateRiskConfig={updateRiskConfig}
                toggleRiskField={() => toggleRiskField(datasetId, selectedRiskField.fieldId)}
                showRiskFieldToggles={showRiskFieldToggles}
            />
        );
    }, [selectedRiskField, setWeighting, updateRiskConfig, toggleRiskField, showRiskFieldToggles, datasetId, setFallbackPosition]);

    if (isLoading || isNull(riskTolerance) || isNull(availableDatasets)) {
        return (
            <div className={styles.riskToleranceDatasetWrapper} data-testid='risk-tolerance-dataset-wrapper'>
                <PageLoadingDots />
            </div>
        );
    }

    if (riskTolerance.length === 0) {
        return (
            <div className={styles.riskToleranceDatasetWrapper} data-testid='risk-tolerance-dataset-wrapper'>
                <div className={styles.riskToleranceDatasetHeader} data-testid='risk-tolerance-dataset-header'>Risk Tolerance: {agreementType}</div>
                <div className={styles.emptyContentWrapper} data-testid='risk-tolerance-dataset-content-wrapper'>
                    There are no fields within this agreement type that are considered to be Risk Fields
                </div>
                {buttonWrapper}
            </div>
        );
    }

    return (
        <>
            <div className={styles.riskToleranceDatasetWrapper} data-testid='risk-tolerance-dataset-wrapper'>
                <div className={styles.riskToleranceDatasetHeader} data-testid='risk-tolerance-dataset-header'>
                    <div className={styles.riskToleranceDatasetTitle}>Risk Tolerance: {agreementType}</div>
                    <div className={styles.riskHelpButtonWrapper}>
                        <CustomTooltip overlayText='Risk Tolerance Guide'>
                            <button className={styles.riskToleranceHelp} onClick={openHelpModal}>?</button>
                        </CustomTooltip>
                    </div>
                </div>
                <div className={styles.contentWrapper} data-testid='risk-tolerance-dataset-content-wrapper'>
                    <div className={styles.fieldsWrapper}>
                        <RiskToleranceDatasetsMenu
                            datasetId={datasetId}
                            setRiskToleranceField={setRiskToleranceField}
                            riskTolerance={riskTolerance}
                            selectedField={selectedField}
                            datasetDefinitions={datasetDefinitions}
                            hiddenFields={hiddenFields}
                            showRiskFieldToggles={showRiskFieldToggles}
                            toggleRiskField={toggleRiskField}
                            documentHiddenFields={documentHiddenFields}
                            allDocumentNames={allDocumentNames}
                            agreementTypeDatasetId={agreementTypeDatasetId}
                        />
                    </div>
                    <div className={styles.configWrapper}>
                        {selectedConfig}
                    </div>
                </div>
                {buttonWrapper}
            </div>
            <HelpModal isOpen={helpModalOpen} closeModal={closeHelpModal} isClientRiskView={showRiskFieldToggles} />
        </>
    );
};
